<template>
	<div v-if="allDataLoadedWithModalPassCode" class="home-wrapper">
		<!-- BEGIN modals on demand -->
		<ja-category-modal
			v-if="selectedCategory"
			:key="selectedCategory.expenseCategory_ID"
			:name="$t('categoryModalName', { category: selectedCategory.name })"
			:show="showCategoryModal && !isPausedSim"
			:category="selectedCategory"
			@closeModal="onCategoryModalClose"
			@modalChanged="onModalChanged"
		></ja-category-modal>
		<ja-life-event-modal
			:name="$t('lifeEventModalName')"
			:show="!!event"
			:event="event"
			@closeModal="onLifeEventModalClose"
			@modalChanged="onModalChanged"
		></ja-life-event-modal>
		<ja-complete-life-stage-modal
			:name="$t('completeLifeStageModalName')"
			:show="!event && showCompleteLifeStageModal"
			@completeLifeStage="onLifeStageCompletion"
			@toggleCompleteLSBtnState="toggleCompleteLSBtnState"
			@toggleLifeStageCompletionModal="toggleLifeStageCompletionModal"
			@modalChanged="onModalChanged"
		></ja-complete-life-stage-modal>
		<ja-forced-life-stage-completion-modal
			:show="showForcedLifeStageCompletionModal"
			:name="$t('completeLifeStageModalName')"
			@completeLifeStage="onLifeStageCompletion"
			@modalChanged="onModalChanged"
		></ja-forced-life-stage-completion-modal>

		<!-- END modals on demand -->
		<!-- BEGIN modals at the beginning of simulation -->
		<ja-life-stage-intro
			v-if="showLifeScenarioModal"
			ref="lifeStageIntroRef"
			:name="$t('lifeStageIntroModalName')"
			:current-step-prev="backCurrentStep"
			:show="showLifeScenarioModal && !!stageSetup"
			:setup-data="stageSetup"
			@closeModal="onLifeScenarioModalClose"
			@modalChanged="onModalChanged"
			@showModalLifeStageIntro="handleModalLifeStageIntro"
		></ja-life-stage-intro>

		<ja-budget-creator
			:show="isConfirmBudgetVisible"
			@confirmModal="onFinalizingBudgetClose"
			@closeModal="onCloseBudget"
		></ja-budget-creator>
		<ja-passcode-modal @onSuccess="onPasscodeUnlock" @backModal="handleToBack">
		</ja-passcode-modal>
		<ja-loans-modal></ja-loans-modal>

		<!-- END modals at the beginning of simulation -->
		<!-- BEGIN modals at the end of simulation -->

		<div v-if="lifestageEnded">
			<ja-student-loan-payment
				v-if="showEducationLoanModal"
				:name="$t('studentLoanModalName')"
				:show="showEducationLoanModal"
				@closeStudenLoanModal="onCloseStudentLoanPayment"
				@modalChanged="onModalChanged"
			></ja-student-loan-payment>
			<ja-credit-card-payment
				v-else-if="showCreditModal"
				:name="$t('creditCardModalName')"
				:show="showCreditModal"
				@closecreditCardLoanModal="onClosecreditCardLoanModal"
				@modalChanged="onModalChanged"
			></ja-credit-card-payment>
			<ja-life-stage-summary
				v-else-if="!userData.hideLifeStageSummaryPopup"
				:name="$t('lifeStageSummaryModalName')"
				:show="!userData.hideLifeStageSummaryPopup"
				@closeLifeStageSummary="onCloseLifeStageSummary"
				@modalChanged="onModalChanged"
				@lifeStageSummary="hideModalLifeStageSummary"
			></ja-life-stage-summary>
			<ja-hopes-dreams-modal
				v-else-if="showHopesAndDreams"
				:name="$t('hopesDreamsModalName')"
				:show="showHopesAndDreams"
				@closeModal="onHopesAndDreamsSelected"
				@modalChanged="onModalChanged"
			></ja-hopes-dreams-modal>
		</div>
		<!-- END modals at the end of simulation -->

		<ja-header
			v-if="!isDashboardView"
			ref="header"
			title="home"
			subtitle="JA Finance Park"
			:show-intro="!showCalendarTutorial && !showWorldTutorial"
			:disabled="isModalOpened"
			:is-tutorial="showCalendarTutorial || showWorldTutorial"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/home"></svgicon>
			</template>
		</ja-header>
		<ja-game-dashboard
			v-if="isDashboardView"
			:is-modal-opened="isModalOpened"
			:disabled="isHomeUiDisabled"
			:finalize-shopping-visibility="showFinalizeShoppingButton"
			:complete-life-stage-button-disabled="completeLifeStageButtonDisabled"
			:is-tutorial-visible="isDashboardTutorialVisible"
			@categorySelected="onCategorySelected"
			@toggleCompleteLSBtnState="toggleCompleteLSBtnState"
			@toggleFinalizeBudgetModal="showConfirmBudgetModal"
			@toggleFinalizeShoppingModal="showShoppingCompletionModal"
		></ja-game-dashboard>
		<content v-else ref="view" :aria-hidden="isModalOpened">
			<div v-if="showCalendarTutorial" class="onboarding-wrapper"></div>
			<ja-sidebar
				:categories="categories"
				:aria-hidden="isHomeUiDisabled"
				:disabled="isHomeUiDisabled"
				:active-category="lastActiveCategoryID"
				@categorySelected="onCategorySelected"
			></ja-sidebar>
			<ja-calendar-button
				:is-tutorial="showCalendarTutorial"
				:aria-hidden="
					showWorldTutorial || isPausedSim || showForcedLifeStageCompletionModal
				"
				:disabled="
					showWorldTutorial ||
						isPausedSim ||
						showForcedLifeStageCompletionModal ||
						isModalOpened
				"
			>
			</ja-calendar-button>
			<div class="world-wrapper">
				<!-- <v-touch @pinchout="zoomIn" @pinchin="zoomOut"> -->
				<ja-world
					v-if="categories"
					:disabled="isHomeUiDisabled"
					aria-hidden="true"
					:categories="categories"
					:zoom="zoom"
					:resources="resources"
					@categorySelected="onCategorySelected"
					@wheel.native="onWheel($event)"
					@worldRendered="onWorldReady"
				></ja-world>
				<!-- </v-touch> -->
				<ja-zoom-control
					:aria-hidden="showCalendarTutorial || isPausedSim"
					:value="zoom"
					:max-zoom="maxZoom"
					:min-zoom="minZoom"
					:is-tutorial="showWorldTutorial"
					:disabled="showCalendarTutorial || isModalOpened"
					@zoomedIn="zoomIn"
					@zoomedOut="zoomOut"
					@openLoan="onCategorySelected"
				></ja-zoom-control>
				<ja-world-tutorial
					v-if="showWorldTutorial"
					ref="tutorial"
					@tutorialEnded="onTutorialEnd"
				></ja-world-tutorial>
			</div>
			<ja-headsup-display
				:aria-hidden="isHomeUiDisabled"
				:disabled="isHomeUiDisabled"
				:finalize-shopping-visibility="showFinalizeShoppingButton"
				:complete-life-stage-button-disabled="completeLifeStageButtonDisabled"
				@toggleCompleteLSBtnState="toggleCompleteLSBtnState"
				@toggleFinalizeBudgetModal="showConfirmBudgetModal"
				@toggleFinalizeShoppingModal="showShoppingCompletionModal"
			></ja-headsup-display>
		</content>
		<ja-tutorial-tooltip
			v-if="isDashboardView && isDashboardTutorialVisible"
			:steps="dashboardTutorialSteps"
			@closeHelper="closeHelper"
		></ja-tutorial-tooltip>
	</div>
</template>

<script>
import gql from 'graphql-tag';
import '../components/icons/zoom-plus';
import '../components/icons/zoom-minus';
import '../components/icons/headerIcons/home';
import UserData from '../mixins/UserData';
import ProfileData from '../mixins/ProfileData';
import LifeStageData from '../mixins/LifeStageData';
import ExpenseCategories from '../mixins/ExpenseCategories';
import ModalFocusManager from '../mixins/ModalFocusManager';
import Header from '../components/ui/headers/Header';
import CategoryModal from '../components/Modal/CategoryModal';
import LifeStageIntro from '../components/Modal/LifeStageIntro';
import LifeStageSummary from '../components/Modal/LifeStageSummary';
import LifeEventModal from '../components/Modal/LifeEventModal';
import Sidebar from '../components/Sidebar/Sidebar';
import HeadsupDisplay from '../components/HeadsupDisplay/HeadsupDisplay';
import World from '../components/World/World';
import ZoomControl from '../components/World/ZoomControl';
import WorldTutorial from '../components/World/WorldTutorial';
import * as PIXI from 'pixi.js-legacy';
import HopesDreamsModal from '../components/Modal/HopesDreamsModal';
import StudentLoanPayment from '../components/Modal/StudentLoanPayment';
import CalendarButton from '../components/Calendar/CalendarButton';
import CreditCardPayment from '../components/Modal/CreditCardPayment';
import CompleteLifeStage from '../components/Modal/CompleteLifeStage';
import ForcedLifeStageCompletion from '../components/Modal/ForcedLifeStageCompletion';
import BudgetCreator from '@/components/LifeStageIntro/BudgetCreator.vue';
import GameDashboard from '@/components/World/GameDashboard.vue';
import TutorialTooltip from '@/components/ui/TutorialTooltip';
import { dashboardTutorialSteps } from '@/helpers/constants';
import StudentsService from '@/services/StudentsService';
import PasscodeModal from '@/components/Modal/PasscodeModal';
import { lockGameStages, simulationNameType } from '@/helpers/constants';
import LoansModal from '@/components/Modal/LoansModal';
import LifeStageService from '@/services/LifeStageService';

export default {
	components: {
		jaSidebar: Sidebar,
		jaHeader: Header,
		jaHeadsupDisplay: HeadsupDisplay,
		// TODO: should be separate component
		jaCategoryModal: CategoryModal,
		jaLifeStageIntro: LifeStageIntro,
		jaLifeStageSummary: LifeStageSummary,
		jaLifeEventModal: LifeEventModal,
		jaHopesDreamsModal: HopesDreamsModal,
		jaStudentLoanPayment: StudentLoanPayment,
		jaCreditCardPayment: CreditCardPayment,
		jaCompleteLifeStageModal: CompleteLifeStage,
		jaForcedLifeStageCompletionModal: ForcedLifeStageCompletion,
		jaBudgetCreator: BudgetCreator,
		jaPasscodeModal: PasscodeModal,
		//TODO: end of separate component
		jaWorld: World,
		jaWorldTutorial: WorldTutorial,
		jaZoomControl: ZoomControl,
		jaCalendarButton: CalendarButton,
		jaGameDashboard: GameDashboard,
		jaTutorialTooltip: TutorialTooltip,
		jaLoansModal: LoansModal
	},
	mixins: [
		UserData,
		LifeStageData,
		ProfileData,
		ExpenseCategories,
		ModalFocusManager
	],
	props: {
		isMapOnboarding: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showCategoryModal: false,
			showLifeScenarioModal: false,
			showHopesDreamsModal: false,
			showMessageResultModal: false,
			showCalendarTooltip: false,
			isDashboardTutorialVisible: false,
			selectedCategory: null,
			zoom: 0.5,
			minZoom: 0.3,
			maxZoom: 1,
			zoomStep: 0.1,
			event: null,
			showEndOfLifeStageAutomatically: false,
			showWorldTutorial: this.isMapOnboarding,
			completeLifeStageButtonDisabled: false,
			showLifeStageCompletionModal: false,
			lastActiveCategoryID: null,
			isConfirmBudgetVisible: false,
			dashboardTutorialSteps: dashboardTutorialSteps,
			ModalLifeStageIntro: false,
			showModalLifeStageSummary: false,
			modalPassCodeUsed: false,
			backCurrentStep: 0
		};
	},
	computed: {
		isDashboardView() {
			// return true;
			return this.$store.getters.isSimulationType('onsite');
		},
		getLockState() {
			return this.$store.getters.getLockState;
		},
		showGuidedTour() {
			return this.$store.getters.showOnboarding;
		},
		lifestageEnded() {
			return (
				!this.lifeStageData.showLifeEventPopUp &&
				this.showEndOfLifeStageModals &&
				!this.isPausedSim
			);
		},
		resources() {
			return this.$store.getters.resources;
		},
		stageSetup() {
			return this.$store.getters.stageSetupData;
		},

		showCreditModal() {
			return (
				!this.lifeStageData.hideCreditCardPaymentPopup &&
				this.profileData.currentCreditScore > 0 &&
				this.lifeStageData.creditCardDebt > 0
			);
		},
		showEducationLoanModal() {
			return (
				!this.lifeStageData.hideLoanPaymentPopup &&
				this.profileData.minimumStudentLoanPayment > 0
			);
		},
		showCompleteLifeStageModal() {
			return (
				this.showLifeStageCompletionModal &&
				!this.lifeStageData.hideLifeStageCompletionPopup &&
				!this.lifeStageData.showLifeEventPopUp
			);
		},
		billboards() {
			return this.$store.getters.billboards;
		},
		showEndOfLifeStageModals() {
			const lsData = this.lifeStageData;
			return (
				(this.showEndOfLifeStageAutomatically &&
					lsData.currentLifeStageCompletionPercentage >= 100) ||
				lsData.hideLifeStageCompletionPopup ||
				(lsData.hideLifeStageSummaryPopup &&
					!this.userData.showKnowledgeQuestionPage) ||
				this.lifeStageIsCompletedPrematurely
			);
		},
		allDataLoaded() {
			// for dashboard view we need only those things
			if (this.isDashboardView) {
				return this.categories && this.lifeStageData && this.profileData;
			}

			return (
				this.categories &&
				this.resources &&
				this.lifeStageData &&
				this.profileData &&
				this.billboards
			);
		},
		allDataLoadedWithModalPassCode() {
			if (this.allDataLoaded) {
				this.modalPassCode();
				return true;
			}
			return false;
		},
		showCalendarTutorial() {
			if (this.allDataLoaded && this.userData) {
				return (
					!this.userData.hideCalendarTutorialPopup &&
					!this.showLifeScenarioModal &&
					!this.isMapOnboarding &&
					!this.showGuidedTour
				);
			}
			return false;
		},
		showHopesAndDreams() {
			return (
				this.lifeStageData.nextLifeStageID &&
				!this.lifeStageData.hideHopesAndDreamsPopup
			);
		},
		isHomeUiDisabled() {
			return (
				this.showWorldTutorial ||
				this.showCalendarTutorial ||
				this.isModalOpened
			);
		},
		isPausedSim() {
			return this.$store.getters.simPauseState;
		},
		showForcedLifeStageCompletionModal() {
			return (
				!this.lifeStageData.hideForcedLifeStageCompletionPopup &&
				this.$store.getters.forcedToNextLifeStageState
			);
		},
		lifeStageIsCompletedPrematurely() {
			return (
				this.lifeStageData.hideForcedLifeStageCompletionPopup &&
				this.$store.getters.forcedToNextLifeStageState
			);
		},
		showFinalizeShoppingButton() {
			return (
				this.lifeStageData &&
				this.lifeStageData.currentLifeStageCompletionPercentage >= 100 &&
				!this.isInBudgetingPhase
			);
		}
	},
	watch: {
		showGuidedTour(value) {
			this.isModalOpened = value;
		},
		categories() {
			if (!this.resources && !this.isDashboardView) {
				this.loadBuildingAssets();
			}
		},
		lifeStageData(data) {
			if (data) {
				if (data.showLifeEventPopUp) {
					let lifeEvent = JSON.parse(localStorage.getItem('lifeEvent'));
					if (lifeEvent) {
						this.event = lifeEvent;
					} else {
						this.generateLifeEvent();
					}
				}

				// If required shopping is completed, show button for Complete Shopping
				// In the ShowShoppingCompletionModal method will be determined whether
				// the EndOfLifeStage modal will be shown or LifeStageCompletion
				// this.hasToShowFinalizeShopping();
			}
		},
		userData(data) {
			if (data && !data.hideBudgetPopupOnHomeScreen) {
				this.$store.dispatch('getLifeStageSetupData');
			}
		},
		isPausedSim(value) {
			this.isModalOpened = value;
		},
		showCalendarTutorial(value) {
			if (
				this.isDashboardView &&
				!this.$store.getters.isLockStage(lockGameStages.BUDGETING)
			) {
				this.isDashboardTutorialVisible = value;
			}
		},
		getLockState(value) {
			if (
				value !== lockGameStages.LIFE_SCENARIO &&
				value !== lockGameStages.LIFE_STAGE_SUMMARY &&
				value !== lockGameStages.BUDGETING &&
				this.isDashboardTutorialVisible
			) {
				this.$store.commit('toggleOnboarding', true);
			} else {
				this.$store.commit('toggleOnboarding', false);
			}
		}
	},

	updated() {
		this.checkForLifeStageData();
		this.checkForCategories();
	},
	created() {
		this.$store.commit('setLoading', true);
		this.loadData();

		if (this.userData && !this.userData.hideBudgetPopupOnHomeScreen) {
			this.$store.dispatch('getLifeStageSetupData');
		}
	},
	async mounted() {
		if (!this.isDashboardView) {
			this.$store.dispatch('getProfileData');
		}

		if (this.categories && !this.resources && !this.isDashboardView) {
			this.loadBuildingAssets();
		}

		if (this.userData && !this.userData.hideBudgetPopupOnHomeScreen) {
			this.showLifeScenarioModal = true;
			this.$store.commit('toggleOnboarding', false);
		} else {
			this.toggleOnboarding();
		}

		// this.hasToShowFinalizeShopping();

		let lsData = this.lifeStageData;
		let userData = this.userData;
		if (
			lsData &&
			!lsData.nextLifeStageID &&
			lsData.hideLifeStageSummaryPopup &&
			userData &&
			!userData.showKnowledgeQuestionPage
		) {
			this.transitionBetweenStages();
		}

		if (this.isMapOnboarding) {
			if (this.$refs.tutorial) {
				this.$refs.tutorial.$el.focus();
			}
		}
	},
	methods: {
		modalPassCode() {
			const totalSavings = this.lifeStageData.studentGoals.reduce(
				(sum, goal) => {
					return sum + (goal.totalSavingsAmount || 0);
				},
				0
			);

			if (
				(this.profileData.isAfterLifeScenarioUnlockedWithPasscode === null ||
					this.profileData.isAfterLifeScenarioUnlockedWithPasscode === false) &&
				totalSavings > 0 &&
				this.userData.hideBudgetPopupOnHomeScreen === true &&
				this.modalPassCodeUsed === false
			) {
				this.modalPassCodeUsed = true;
				this.$store.dispatch('hideModalLifeStageIntro');
			}

			if (
				(this.profileData.isAfterSetBudgetUnlockedWithPasscode === null ||
					this.profileData.isAfterSetBudgetUnlockedWithPasscode === false) &&
				this.profileData.isAfterLifeScenarioUnlockedWithPasscode === true &&
				this.$store.getters.areAllBudgetsSet &&
				this.userData.hideConfirmBudgetPopup &&
				this.modalPassCodeUsed === false
			) {
				this.modalPassCodeUsed = true;
				this.$store.dispatch('hideModalConfirmBudgetVisible');
			}
		},
		async onPasscodeUnlock(lockState) {
			if (lockState === lockGameStages.BUDGETING) {
				this.$store.commit('setLoading', true);
				this.modalPassCodeUsed = true;
				this.$store.dispatch('showModalConfirmBudgetVisible');
				await LifeStageService.updateBudgetModalVisibility(
					this.userData.studentId,
					true
				);

				// TODO: Revalidate this solution

				this.$store.dispatch('getUserData').then(() => {
					this.$store.commit('resetCategories');
					this.$store.commit('resetLifeStageData');
					this.checkForLifeStageData(true);
					this.checkForCategories(true);
					this.isConfirmBudgetVisible = false;
					this.$store.commit('setLoading', false);
				});
			}
		},
		handleToBack(index) {
			if (index == 3) {
				this.$store.dispatch('hideModalConfirmBudgetVisible');
				this.isConfirmBudgetVisible = true;
			} else if (index !== 0) {
				this.$refs.lifeStageIntroRef.prevStep();
				this.$store.dispatch('getLifeStageSetupData');
				this.showModalLifeStageIntro = true;
				this.showLifeScenarioModal = true;
			} else {
				this.$store.dispatch('getLifeStageSetupData');
				this.showModalLifeStageIntro = true;
				this.showLifeScenarioModal = true;
				this.backCurrentStep = 2;
			}
		},
		handleModalLifeStageIntro() {
			this.showModalLifeStageIntro = true;
			this.$store.dispatch('showModalPassCode');
			this.$store.dispatch('hideModalLifeStageIntro');
		},
		hideModalLifeStageSummary() {
			this.showModalLifeStageSummary = true;
			this.profileData.isAfterLifeStageSummaryUnlockedWithPasscode = null;
			this.$store.dispatch('hideModalLifeStageSummary');
		},
		async closeHelper() {
			const tooltipsStatusInfo = {
				id: this.userData.studentId,
				hideCalendarTutorialPopup: true
			};

			await StudentsService.updateTooltipStatus(tooltipsStatusInfo);

			this.isDashboardTutorialVisible = false;

			this.onTutorialEnd();
		},
		toggleCompleteLSBtnState() {
			this.completeLifeStageButtonDisabled = !this
				.completeLifeStageButtonDisabled;
			this.toggleLifeStageCompletionModal();
		},
		toggleLifeStageCompletionModal() {
			this.showLifeStageCompletionModal = !this.showLifeStageCompletionModal;
		},
		onTutorialEnd() {
			this.showWorldTutorial = false;
		},
		loadData() {
			this.checkForProfileData();
			this.checkForLifeStageData();
			this.checkForCategories();
		},
		onWorldReady() {
			if (this.profileData) {
				this.$store.commit('setLoading', false);
			}
		},
		// TODO: Delete this. Might not be needed
		hasToShowFinalizeShopping() {
			if (
				this.lifeStageData &&
				this.lifeStageData.currentLifeStageCompletionPercentage >= 100 &&
				!this.isInBudgetingPhase
			) {
				this.showFinalizeShoppingButton = true;
			}
		},
		loadBuildingAssets() {
			PIXI.utils.clearTextureCache();
			const loader = new PIXI.Loader();

			loader.add(
				'world-top-left',
				require('@/assets/images/world/map/world-top-left.png')
			);
			loader.add(
				'world-top-right',
				require('@/assets/images/world/map/world-top-right.png')
			);
			loader.add(
				'world-bottom-left',
				require('@/assets/images/world/map/world-bottom-left.png')
			);
			loader.add(
				'world-bottom-right',
				require('@/assets/images/world/map/world-bottom-right.png')
			);
			loader.add(
				'local-sponsor-billboard',
				require('@/assets/images/world/buildings/local-sponsor-billboard.png')
			);

			loader.add(
				'small-billboard',
				require('@/assets/images/world/buildings/small-billboard.png')
			);

			loader.add(
				'small-default-billboard',
				require('@/assets/images/small-default-billboard.png')
			);
			this.categories.forEach(category => {
				try {
					loader.add(
						category.assetName,
						require(`@/assets/images/world/buildings/${category.assetName}.png`)
					);
				} catch (ex) {
					/*eslint-disable */
					console.warn(
						`Asset @/assets/images/world/buildings/${category.assetName}.png doesn't exist`
					);
					/*eslint-enable */
				}
				if (this.billboards[category.expenseCategory_ID]) {
					loader.add(
						`billboard_${category.assetName}`,
						this.billboards[category.expenseCategory_ID]
					);
				}
			});

			loader.load((loader, resources) => {
				this.$store.commit('setResources', resources);
			});
		},
		onWheel(event) {
			if (event.deltaY < 0) {
				this.zoomIn();
			} else if (event.deltaY > 0) {
				this.zoomOut();
			}
		},
		zoomOut() {
			if (this.zoom > this.minZoom) {
				this.zoom = Math.round((this.zoom - this.zoomStep) * 10) / 10;
			}
		},
		zoomIn() {
			if (this.zoom < this.maxZoom) {
				this.zoom = Math.round((this.zoom + this.zoomStep) * 10) / 10;
			}
		},
		onCategoryModalClose(categoryID) {
			this.showCategoryModal = false;
			this.lastActiveCategoryID = categoryID;
		},
		onLifeScenarioModalClose() {
			this.$store.commit('setLoading', true);
			this.$store.commit('resetCategories');
			this.$store.commit('resetLifeStageData');
			this.showLifeScenarioModal = false;
			this.toggleOnboarding();
			this.isModalOpened = false;
			this.loadData();
		},
		toggleOnboarding() {
			if (!this.userData.hideOnboardingTutorialPopup) {
				this.$store.commit('toggleOnboarding', true);
			}
		},
		onCloseBudget() {
			this.isConfirmBudgetVisible = false;
		},
		async onFinalizingBudgetClose() {
			if (
				this.$store.getters.simulationType ===
					simulationNameType.ADVANCED_VIRTUAL ||
				this.$store.getters.simulationType === simulationNameType.ENTRY_VIRTUAL
			) {
				this.$store.commit('setLoading', true);
				const response = await LifeStageService.updateBudgetModalVisibility(
					this.userData.studentId,
					true
				);

				if (response.data) {
					// TODO: Revalidate this solution

					this.$store.dispatch('getUserData').then(() => {
						this.$store.dispatch('hideModalConfirmBudgetVisible');
						this.$store.commit('resetCategories');
						this.$store.commit('resetLifeStageData');
						this.checkForLifeStageData(true);
						this.checkForCategories(true);
						this.isConfirmBudgetVisible = false;
						this.$store.commit('setLoading', false);
					});
				}
			} else {
				this.$store.dispatch('showModalPassCode');
				this.$store.dispatch('hideModalConfirmBudgetVisible');
				this.isConfirmBudgetVisible = false;
			}
		},
		onCategorySelected(category) {
			this.lastActiveCategoryID = null;
			this.selectedCategory = category;
			this.showCategoryModal = true;
		},
		async generateLifeEvent() {
			const response = await this.$apollo.query({
				query: gql`
					{
						students(where: "id=${this.userData.studentId}") {
							randomCmsLifeEvent {
								cmsLifeEventAccountType{
									cmsAccountType{
										accountType_ID
										name
									}
								}
    							lifeEvent_ID
								amount
								description
								isAmountAdded
								mustChooseAccount
							}
						}
					}
				`,
				fetchPolicy: 'no-cache'
			});

			if (response.data) {
				this.event = response.data.students[0].randomCmsLifeEvent;
				localStorage.setItem('lifeEvent', JSON.stringify(this.event));
			}
		},
		async transitionBetweenStages() {
			this.$store.commit('setLoading', true);

			var isSimulationCompleted = !this.lifeStageData.nextLifeStageID;

			var isCurrentLifeStageForced = false;

			if (this.lifeStageIsCompletedPrematurely) {
				isCurrentLifeStageForced = true;
			}
			const lifeStageInfo = {
				id: this.lifeStageData.id,
				lifeStage_ID: isSimulationCompleted
					? this.lifeStageData.lifeStage_ID
					: this.lifeStageData.nextLifeStageID,
				isSimulationCompleted: isSimulationCompleted,
				hideLifeStageSummaryPopup: false,
				hideLoanPaymentPopup: false,
				hideCreditCardPaymentPopup: false,
				hideBudgetPopupOnHomeScreen: false,
				hideHopesAndDreamsPopup: false,
				hideLifeStageCompletionPopup: false,
				hideForcedLifeStageCompletionPopup: false,
				isCurrentLifeStageForced: isCurrentLifeStageForced,
				isTransferToNextLifeStagePending: false,
				isAfterLifeScenarioUnlockedWithPasscode: false,
				isAfterLifeStageSummaryUnlockedWithPasscode: false,
				isAfterSetBudgetUnlockedWithPasscode: false
			};

			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateStudent(update: $update) {
							lifeStage_ID
						}
					}
				`,
				variables: {
					update: lifeStageInfo
				}
			});

			if (response.data) {
				if (!this.lifeStageData.nextLifeStageID) {
					this.$router.push('/posttest');
				} else {
					this.$store.dispatch('getUserData').then(() => {
						this.showEndOfLifeStageAutomatically = false;
						this.showLifeScenarioModal = true;
						this.$store.commit('resetCategories');
						this.$store.commit('resetLifeStageData');
						this.$store.commit('resetProfileData');
						this.loadData();
					});
				}
			}
		},
		onLifeEventModalClose() {
			this.event = null;
			this.$store.dispatch('getLifeStageData');
		},
		onHopesAndDreamsSelected() {
			if (this.lifeStageData.nextLifeStageID) {
				this.transitionBetweenStages();
			}
		},
		onCloseLifeStageSummary() {
			this.userData.hideLifeStageSummaryPopup = true;
			if (!this.lifeStageData.nextLifeStageID) {
				this.transitionBetweenStages();
			}
		},
		onCloseStudentLoanPayment() {
			this.$store.dispatch('getLifeStageData').then(() => {
				this.lifeStageData.hideLoanPaymentPopup = true;
			});
		},
		onClosecreditCardLoanModal() {
			this.$store.dispatch('getLifeStageData');
			this.lifeStageData.hideCreditCardPaymentPopup = true;
		},
		onLifeStageCompletion() {
			this.showEndOfLifeStageAutomatically = true;
		},
		showConfirmBudgetModal() {
			this.isConfirmBudgetVisible = true;
		},
		showShoppingCompletionModal() {
			if (
				this.profileData &&
				this.profileData.cmsLifeStage.displayOrder === 1 &&
				!this.lifeStageData.hideLifeStageCompletionPopup
			) {
				this.showLifeStageCompletionModal = true;
			} else {
				this.showEndOfLifeStageAutomatically = true;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.home-wrapper {
	font-family: 'RobotoRegular';
	height: 100%;
	max-height: 100%;
	display: flex;
	flex-direction: column;
	background-repeat: no-repeat;

	content {
		height: calc(100vh - 100px);
		max-height: calc(100vh - 100px);
		display: flex;
		align-items: flex-start;
		position: relative;

		.onboarding-wrapper {
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			z-index: 9;
		}

		.sidebar {
			z-index: 3;
		}

		.headsup-display {
			position: absolute;
			z-index: 2;
			bottom: 0;
			left: 0;
			right: 0;
			margin: auto;
		}

		.world-wrapper {
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
		}

		.zoom-control {
			top: 32px;
			right: 24px;
		}
	}
}
</style>
